/*
 * Settings → Notifications as sections of rows: registered types grouped by
 * their `group`, with types that share a `preference_row` folded into one
 * row that saves every key at once.
 */
import type {
  EmailPolicy,
  NotificationPreferences,
  NotificationType,
  TypePreference
} from "./types";

export const EMAIL_POLICY_LABELS: Record<EmailPolicy, string> = {
  always: "Always",
  offline: "Only When Away",
  never: "Never"
};

export const OTHER_GROUP = { id: "other", label: "Other" };

export type PreferenceRow = {
  /** The shared `preference_row`, or the type key. */
  id: string;
  /** Every type key the row saves. */
  keys: string[];
  label: string;
  description: string;
  defaults: TypePreference;
};

export type PreferenceSection = {
  id: string;
  label: string;
  rows: PreferenceRow[];
};

const defaultsOf = (t: NotificationType): TypePreference => ({
  in_app: t.default_in_app,
  email: t.default_email
});

/** Sections in the server's order, then Other; rows in registry order. */
export const buildSections = (
  prefs: Pick<NotificationPreferences, "types" | "groups">,
  exclude: string[] = []
): PreferenceSection[] => {
  const groups = prefs.groups ?? [];
  const known = new Set(groups.map((g) => g.id));
  const sections = new Map<string, PreferenceSection>(
    [...groups, OTHER_GROUP].map((g) => [g.id, { ...g, rows: [] }])
  );
  const rows = new Map<string, PreferenceRow>();
  for (const t of prefs.types.filter((type) => !exclude.includes(type.key))) {
    const id = t.preference_row || t.key;
    const existing = rows.get(id);
    if (existing) {
      existing.keys.push(t.key);
    } else {
      const row: PreferenceRow = {
        id,
        keys: [t.key],
        label: t.label,
        description: t.description,
        defaults: defaultsOf(t)
      };
      rows.set(id, row);
      const group = t.group && known.has(t.group) ? t.group : OTHER_GROUP.id;
      sections.get(group)?.rows.push(row);
    }
  }
  return [...sections.values()];
};

/** The row's current value: its first key's effective preference. */
export const rowValue = (
  row: PreferenceRow,
  perType: Record<string, TypePreference>
): TypePreference => perType[row.keys[0]] ?? row.defaults;

export const isRowDefault = (row: PreferenceRow, value: TypePreference) =>
  value.in_app === row.defaults.in_app && value.email === row.defaults.email;

/** The `per_type` patch that sets every key in the row. */
export const rowPatch = (
  row: PreferenceRow,
  value: TypePreference
): Record<string, TypePreference> =>
  Object.fromEntries(row.keys.map((k) => [k, { ...value }]));
